
import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getMasterDatabaseRecords,
  getMasterDatabaseRecordById,
  createMasterDatabaseRecord as createMasterDatabaseRecordApi,
  importMasterDatabaseRecords as importMasterDatabaseRecordsApi,
  updateMasterDatabaseRecord,
} from "../api/masterDatabase.api";
import { startLoading, stopLoading } from "@/store/loaderSlice";

import type {
  GetMasterDatabaseParams,
  MasterDatabaseListResponse,
  MasterDatabaseRecordResponse,
  MasterDatabaseImportResponse,
  MasterDatabaseRecordPayload,
} from "../api/masterDatabase.api";

type RejectValue = {
  rejectValue: string;
};

const getErrorMessage = (
  error: any,
  fallback: string,
): string => {
  return (
    error?.response?.data?.message ||
    error?.message ||
    error?.error ||
    fallback
  );
};

/**
 * Fetch Master DB records with pagination, search and statistics.
 */
export const fetchMasterDatabaseRecords = createAsyncThunk<
  MasterDatabaseListResponse,
  GetMasterDatabaseParams | undefined,
  RejectValue
>(
  "masterDatabase/fetchRecords",
  async (params, thunkAPI) => {
    try {
      return await getMasterDatabaseRecords(params ?? {});
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(
          error,
          "Failed to fetch Master Database records",
        ),
      );
    }
  },
);

/**
 * Fetch a single Master DB record by ID.
 */
export const fetchMasterDatabaseRecordById = createAsyncThunk<
  MasterDatabaseRecordResponse,
  string,
  RejectValue
>(
  "masterDatabase/fetchRecordById",
  async (id, thunkAPI) => {
    try {
      return await getMasterDatabaseRecordById(id);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(
          error,
          "Failed to fetch Master Database record",
        ),
      );
    }
  },
);

/**
 * Create a manually entered Master DB record.
 */
export const createMasterDatabaseRecordThunk = createAsyncThunk<
  MasterDatabaseRecordResponse,
  MasterDatabaseRecordPayload,
  RejectValue
>(
  "masterDatabase/createRecord",
  async (payload, thunkAPI) => {
    try {
      return await createMasterDatabaseRecordApi(payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(
          error,
          "Failed to create Master Database record",
        ),
      );
    }
  },
);

/**
 * Import records from an Excel or CSV file.
 */
export const importMasterDatabaseRecords = createAsyncThunk<
  MasterDatabaseImportResponse,
  File,
  RejectValue
>(
  "masterDatabase/importRecords",
  async (file, thunkAPI) => {
    thunkAPI.dispatch(startLoading());

    try {
      return await importMasterDatabaseRecordsApi(file);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(
          error,
          "Failed to import Master Database records",
        ),
      );
    } finally {
      thunkAPI.dispatch(stopLoading());
    }
  }
);

/**
 * Update an existing Master DB record.
 */
export const updateMasterDatabaseRecordThunk = createAsyncThunk<
  MasterDatabaseRecordResponse,
  {
    id: string;
    payload: MasterDatabaseRecordPayload;
  },
  RejectValue
>(
  "masterDatabase/updateRecord",
  async ({ id, payload }, thunkAPI) => {
    try {
      return await updateMasterDatabaseRecord(id, payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(
          error,
          "Failed to update Master Database record",
        ),
      );
    }
  },
);
